import { MailFilled } from "@ant-design/icons";
import { visibleComponentKeys } from "@p0security/shared/install/options";
import {
  AwsIconUrl,
  AwsLambdaIconUrl,
  AwsRdsIconUrl,
  AzureIconUrl,
  CiscoIconUrl,
  CustomIconUrl,
  DatadogIconUrl,
  EntraIdIconUrl,
  GcloudIconUrl,
  GithubIconUrl,
  GoogleWorkspaceIconUrl,
  IncidentIoIconUrl,
  JiraIconUrl,
  K8sIconUrl,
  McpIconUrl,
  MysqlIconUrl,
  OktaIconUrl,
  OracleIconUrl,
  PagerdutyIconUrl,
  PostgresIconUrl,
  RdpIconUrl,
  SelfHostedIconUrl,
  SnowflakeIconUrl,
  SplunkIconUrl,
  SshIconUrl,
  TailscaleIconUrl,
  WindowsIconUrl,
  ZscalerIconUrl,
} from "@p0security/shared/integrations/constants";
import { EntraIdComponents } from "@p0security/shared/integrations/directories/azure-ad/components";
import { OktaComponents } from "@p0security/shared/integrations/directories/okta/components";
import { AwsLambdaNotifierComponents } from "@p0security/shared/integrations/notifiers/aws-lambda/components";
import { AwsRdsComponents } from "@p0security/shared/integrations/resources/aws-rds/components";
import { AwsComponents } from "@p0security/shared/integrations/resources/aws/components";
import { AzureComponents } from "@p0security/shared/integrations/resources/azure/components";
import { CsaComponents } from "@p0security/shared/integrations/resources/csa/components";
import { CustomIntegrationComponents } from "@p0security/shared/integrations/resources/custom/components";
import { GcloudComponents } from "@p0security/shared/integrations/resources/gcloud/components";
import { McpComponents } from "@p0security/shared/integrations/resources/mcp/components";
import { MysqlComponents } from "@p0security/shared/integrations/resources/mysql/components";
import { PostgresComponents } from "@p0security/shared/integrations/resources/postgres/components";
import { RdpComponents } from "@p0security/shared/integrations/resources/rdp/components";
import { SelfHostedComponents } from "@p0security/shared/integrations/resources/self-hosted/components";
import { SnowflakeComponents } from "@p0security/shared/integrations/resources/snowflake/components";
import { SplunkStackComponents } from "@p0security/shared/integrations/resources/splunk-stack/components";
import { SshComponents } from "@p0security/shared/integrations/resources/ssh/components";
import { TailscaleComponents } from "@p0security/shared/integrations/resources/tailscale/components";
import { WindowsComponents } from "@p0security/shared/integrations/resources/windows/components";
import { ZpaComponents } from "@p0security/shared/integrations/resources/zpa/components";
import { DOCS_URLS } from "constants/docsUrls";
import { compact } from "lodash";

import { SlackLogo } from "./Slack/Slack";
import { TeamsLogo } from "./Teams/Teams";
import { WebexLogo } from "./Webex/Webex";
import { IntegrationData } from "./types";

export const trackers: IntegrationData[] = [
  {
    key: "jira",
    label: "Jira",
    icon: JiraIconUrl,
    docs: {
      install: DOCS_URLS.JIRA_INSTALL,
    },
  },
];

export const siems: IntegrationData[] = [
  {
    key: "splunk",
    label: "Splunk",
    icon: SplunkIconUrl,
    docs: {
      install: DOCS_URLS.SPLUNK_INSTALL,
    },
    availableComponents: ["audit-log"],
  },
  {
    key: "datadog",
    label: "Datadog",
    icon: DatadogIconUrl,
    docs: {
      install: "",
    },
    availableComponents: ["audit-log"],
  },
];

export const approvers: IntegrationData[] = [
  {
    key: "pagerduty",
    label: "Pagerduty",
    icon: PagerdutyIconUrl,
    docs: {
      install: DOCS_URLS.PAGERDUTY_INSTALL,
    },
    isPro: true,
  },
  {
    key: "incidentio",
    label: "Incident.io",
    icon: IncidentIoIconUrl,
    docs: {
      install: DOCS_URLS.INCIDENTIO_INSTALL,
    },
    isPro: true,
    availableComponents: ["root"],
  },
];

export const notifiers: IntegrationData[] = [
  {
    key: "slack",
    label: "Slack",
    icon: <SlackLogo />,
    docs: {
      install: DOCS_URLS.SLACK_INSTALL,
    },
  },
  {
    key: "ms-teams",
    label: "Microsoft Teams",
    icon: <TeamsLogo />,
    docs: {
      install: DOCS_URLS.TEAMS_INSTALL,
    },
  },
  {
    key: "webex",
    label: "Webex",
    icon: <WebexLogo />,
    docs: {
      install: DOCS_URLS.WEBEX_INSTALL,
    },
  },
  {
    key: "aws-lambda-notifier",
    label: "AWS Lambda",
    icon: AwsLambdaIconUrl,
    availableComponents: visibleComponentKeys(AwsLambdaNotifierComponents),
    docs: {
      install: DOCS_URLS.AWS_LAMBDA_NOTIFIER_INSTALL,
    },
  },
  {
    key: "email",
    label: "Email",
    icon: <MailFilled style={{ fontSize: 20 }} />,
    docs: {
      install: DOCS_URLS.EMAIL_INSTALL,
    },
  },
];

export const directories: IntegrationData[] = [
  {
    key: "workspace",
    label: "Google Workspace",
    icon: GoogleWorkspaceIconUrl,
    docs: {
      install: DOCS_URLS.GOOGLE_WORKSPACE_INSTALL,
    },
    isPro: true,
  },
  {
    key: "azure-ad",
    label: "Entra ID (Legacy)",
    icon: EntraIdIconUrl,
    availableComponents: visibleComponentKeys(EntraIdComponents),
    docs: {
      install: DOCS_URLS.ENTRA_ID_LEGACY_INSTALL,
    },
    isPro: true,
  },
  {
    key: "entra-id",
    label: "Entra ID",
    icon: EntraIdIconUrl,
    availableComponents: visibleComponentKeys(EntraIdComponents),
    docs: {
      install: DOCS_URLS.ENTRA_ID_INSTALL,
    },
    isPro: true,
  },
  {
    key: "okta",
    label: "Okta",
    icon: OktaIconUrl,
    availableComponents: visibleComponentKeys(OktaComponents),
    docs: {
      install: DOCS_URLS.OKTA_INSTALL,
    },
    isPro: true,
  },
];

export const resourceIntegrations: (
  flags?: Record<string, boolean>
) => IntegrationData[] = (flags) => {
  const baseResources = [
    {
      key: "aws",
      label: "AWS",
      icon: AwsIconUrl,
      availableComponents: visibleComponentKeys(AwsComponents),
      docs: {
        install: DOCS_URLS.AWS_INSTALL,
        request: DOCS_URLS.AWS_REQUEST,
      },
    },
    {
      key: "aws-rds",
      label: "AWS RDS",
      icon: AwsRdsIconUrl,
      availableComponents: visibleComponentKeys(AwsRdsComponents),
      docs: {
        install: "https://docs.p0.dev/integrations/iam-integrations/aws-rds",
        request:
          "https://docs.p0.dev/integrations/iam-integrations/aws-rds/requesting-access",
      },
    },
    {
      key: "azure",
      label: "Azure",
      icon: AzureIconUrl,
      availableComponents: visibleComponentKeys(AzureComponents),
      docs: {
        install: DOCS_URLS.AZURE_INSTALL,
        request: DOCS_URLS.AZURE_REQUEST,
      },
    },
    {
      key: "gcloud",
      label: "Google Cloud",
      icon: GcloudIconUrl,
      availableComponents: visibleComponentKeys(GcloudComponents, flags),
      docs: {
        install: DOCS_URLS.GCLOUD_INSTALL,
        request: DOCS_URLS.GCLOUD_REQUEST,
      },
    },
    flags?.githubJitIntegration && {
      key: "github",
      label: "GitHub",
      icon: GithubIconUrl,
      docs: {
        install: DOCS_URLS.GITHUB_INSTALL,
        request: DOCS_URLS.GITHUB_REQUEST,
      },
    },
    {
      key: "custom",
      label: "Custom",
      icon: CustomIconUrl,
      availableComponents: visibleComponentKeys(CustomIntegrationComponents),
      docs: {
        install: DOCS_URLS.CUSTOM_INSTALL,
        request: DOCS_URLS.CUSTOM_REQUEST,
      },
    },
    flags?.stackAccessUiIntegration && {
      key: "splunk-stack",
      label: "Splunk Stack Access",
      icon: SplunkIconUrl,
      availableComponents: visibleComponentKeys(SplunkStackComponents),
      docs: {
        install:
          "https://docs.p0.dev/integrations/iam-integrations/splunk-stack",
        request:
          "https://docs.p0.dev/integrations/iam-integrations/splunk-stack/requesting-access",
      },
    },
    flags?.selfHostedIntegration && {
      key: "self-hosted",
      label: "Self-hosted",
      icon: SelfHostedIconUrl,
      availableComponents: visibleComponentKeys(SelfHostedComponents),
      docs: {
        install: DOCS_URLS.SELF_HOSTED_INSTALL,
      },
    },
    {
      key: "k8s",
      label: "Kubernetes",
      icon: K8sIconUrl,
      availableComponents: ["iam-write"],
      docs: {
        install: DOCS_URLS.K8S_INSTALL,
        request: DOCS_URLS.K8S_REQUEST,
      },
    },
    {
      key: "mcp",
      label: "MCP gateway",
      icon: McpIconUrl,
      availableComponents: visibleComponentKeys(McpComponents),
      docs: {},
    },
    {
      key: "mysql",
      label: "MySQL",
      icon: MysqlIconUrl,
      availableComponents: visibleComponentKeys(MysqlComponents),
      docs: {
        install: "https://docs.p0.dev/integrations/iam-integrations/mysql",
        request:
          "https://docs.p0.dev/integrations/iam-integrations/mysql/requesting-access",
      },
    },
    flags?.enableOracleIntegration && {
      key: "oci",
      label: "Oracle Cloud",
      icon: OracleIconUrl,
      availableComponents: ["iam-write"],
      docs: {},
    },
    {
      key: "snowflake",
      label: "Snowflake",
      icon: SnowflakeIconUrl,
      availableComponents: visibleComponentKeys(SnowflakeComponents),
      docs: {
        install: DOCS_URLS.SNOWFLAKE_INSTALL,
      },
    },
    {
      key: "pg",
      label: "PostgreSQL",
      icon: PostgresIconUrl,
      availableComponents: visibleComponentKeys(PostgresComponents),
      docs: {
        install: DOCS_URLS.POSTGRES_INSTALL,
      },
    },
    {
      key: "ssh",
      label: "SSH",
      icon: SshIconUrl,
      availableComponents: visibleComponentKeys(SshComponents),
      docs: {
        install: DOCS_URLS.SSH_INSTALL,
      },
    },
    flags?.enableRdpIntegration && {
      key: "rdp",
      label: "RDP",
      icon: RdpIconUrl,
      availableComponents: visibleComponentKeys(RdpComponents),
      docs: {},
    },
    {
      key: "windows",
      label: "Windows",
      icon: WindowsIconUrl,
      availableComponents: visibleComponentKeys(WindowsComponents),
      docs: {},
    },
    flags?.tailscaleIntegration && {
      key: "tailscale",
      label: "Tailscale",
      icon: TailscaleIconUrl,
      availableComponents: visibleComponentKeys(TailscaleComponents),
      docs: {
        install: DOCS_URLS.TAILSCALE_INSTALL,
      },
    },
    flags?.enableZscalerIntegration && {
      key: "zscaler-private-access",
      label: "Zscaler Private Access",
      icon: ZscalerIconUrl,
      availableComponents: visibleComponentKeys(ZpaComponents),
      docs: {
        install: DOCS_URLS.ZSCALER_INSTALL,
      },
    },
    flags?.enableCiscoSecureAccessIntegration && {
      key: "cisco-secure-access",
      label: "Cisco Secure Access",
      icon: CiscoIconUrl,
      availableComponents: visibleComponentKeys(CsaComponents),
      docs: {
        install:
          "https://docs.p0.dev/integrations/iam-integrations/cisco-secure-access",
      },
    },
  ];

  return compact(baseResources);
};

export const allGaIntegrations = Object.fromEntries(
  [
    ...approvers,
    ...directories,
    ...notifiers,
    ...resourceIntegrations({}),
    ...siems,
    ...trackers,
  ].map((integration) => [integration.key, integration] as const)
);
